import { useMemo } from 'react';
import styled from 'styled-components';
import { getBrandIconUrl } from '../../../../../../../constants/brand/brandIconMapping.constant';
import { IMarketPriceFilter } from '../../../../../../../constants/filter/marketPriceFilter.constant';
import { useGetMachinePriceStatisticsMeta } from '../../../../../../../hooks/api/marketPrice/queries/useGetMachinePriceStatisticsMeta.query';
import { FilterOptionType, FilterType, IFilter } from '../../../../../../../interfaces/Filter.interfaces';
import { createMarketPriceFilterSearchParams } from '../../../../../../../utils/filter/createMarketPriceFilterSearchParams.util';

interface BrandIconFilterShortVerProps {
  filter: IFilter;
  filterType: FilterType;
  option: FilterOptionType;
  handleClick: () => void;
  localFilters: IFilter[];
}

export const BrandIconFilterShortVer = ({
  filter,
  filterType,
  option,
  handleClick,
  localFilters,
}: BrandIconFilterShortVerProps) => {
  const {
    data: preMarketPriceProducts,
    isLoading: isPreMarketPriceProductsLoading,
    isSuccess: isPreMarketPriceProductsSuccess,
  } = useGetMachinePriceStatisticsMeta({
    searchParam: createMarketPriceFilterSearchParams({ filters: localFilters as IMarketPriceFilter[] }),
    options: {
      enabled: filterType === 'marketPriceFilter' && filter.key === 'filter_brand',
      staleTime: 1000 * 60 * 60 * 5,
    },
  });

  const marketPriceProductsByBrandCount = useMemo<string>(() => {
    if (isPreMarketPriceProductsLoading) {
      return '...';
    }

    if (isPreMarketPriceProductsSuccess && preMarketPriceProducts) {
      return `(${preMarketPriceProducts.brands.find((brand) => brand.id === option.value)?.tractorCount || 0})`;
    }

    return '';
  }, [preMarketPriceProducts, isPreMarketPriceProductsLoading, isPreMarketPriceProductsSuccess, option.value]);

  // 브랜드명에서 괄호 안의 텍스트 추출
  const { brandName, tooltipText } = useMemo(() => {
    const optionNameStr = String(option.optionName);
    const match = optionNameStr.match(/^(.+?)\((.+?)\)$/);

    if (match) {
      return {
        brandName: match[1].trim(),
        tooltipText: match[2].trim(),
      };
    }

    return {
      brandName: optionNameStr,
      tooltipText: null,
    };
  }, [option.optionName]);

  if (filter.key !== 'filter_brand') {
    return null;
  }

  return (
    <Container onClick={handleClick} isSelected={option.isSelected}>
      <BrandContent>
        {tooltipText && (
          <BrandTooltip>
            <TooltipText>{tooltipText}</TooltipText>
          </BrandTooltip>
        )}
        <BrandIconWrapper isSelected={option.isSelected}>
          <BrandIcon src={getBrandIconUrl(String(option.optionName))} alt={String(option.optionName)} />
        </BrandIconWrapper>
        {filterType === 'marketPriceFilter' && marketPriceProductsByBrandCount && (
          <CountBadge>
            <CountText>{marketPriceProductsByBrandCount.replace(/[()]/g, '')}</CountText>
          </CountBadge>
        )}
      </BrandContent>

      <BrandInfo>
        <BrandName>{brandName}</BrandName>
      </BrandInfo>
    </Container>
  );
};

const Container = styled.div<{ isSelected: boolean }>`
  display: flex;
  width: 50px;
  min-width: 50px;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  gap: 3px;

  cursor: pointer;
`;

const BrandContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 12px;
  position: relative;
`;

const BrandTooltip = styled.div`
  display: flex;
  height: 18px;
  padding: 4px 6px;
  align-items: center;
  gap: 2px;

  position: absolute;
  left: 50%;
  top: -8px;
  transform: translateX(-50%);

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-offblack, #505363);

  white-space: nowrap;
  z-index: 5;
`;

const TooltipText = styled.span`
  color: #fff;
  text-align: center;

  font-family: Pretendard;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
`;

const BrandIconWrapper = styled.div<{ isSelected: boolean }>`
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: var(--radius-round, 80px);
  border: 1px solid
    ${(props) =>
      props.isSelected
        ? 'var(--Light-Scheme-Color-primary-blue, #445EFF)'
        : 'var(--Light-Scheme-Monochrome-grey30, #e0e0eb)'};
  background: ${(props) =>
    props.isSelected
      ? 'var(--Light-Scheme-Color-secondary-blue, #E5E9FF)'
      : 'var(--Light-Scheme-Monochrome-white, #fff)'};

  overflow: hidden;
`;

const BrandIcon = styled.img`
  width: 40px;
  height: 36px;

  object-fit: contain;
`;

const BrandInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BrandName = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */

  white-space: nowrap;
`;

const CountBadge = styled.div`
  display: flex;
  height: 16px;
  min-width: 16px;
  padding: 0 4px;
  justify-content: center;
  align-items: center;
  gap: 16px;

  position: absolute;
  right: 0;
  top: 0;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Color-primary-blue, #445eff);
`;

const CountText = styled.span`
  color: #fff;
  text-align: center;

  /* System/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 10.2px */
`;
